import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { slugify, PUBLIC_TAGS } from '../lib/slug';

interface Hour { days: number[]; start: string; end: string }

const TEMPLATES = ['casual', 'contemporaneo', 'classico'] as const;
const DOW = [0, 1, 2, 3, 4, 5, 6] as const;
const COUNTRIES = ['Brasil', 'Portugal', 'España', 'France', 'United States', 'Argentina', 'México', 'Chile', 'Uruguay', 'Italia'];
const DESC_MIN = 1100;

export function PublicSitePage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];

  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState<Hour[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [template, setTemplate] = useState('casual');
  const [showPrices, setShowPrices] = useState(true);
  const [published, setPublished] = useState(false);
  const [slug, setSlug] = useState('');
  const [savedMsg, setSavedMsg] = useState(false);

  const load = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase.from('restaurants')
      .select('slug, country, state, city, public_address, public_maps_url, public_phone, public_description, public_hours, public_tags, public_template, public_show_prices, public_published')
      .eq('id', restaurant.id).single();
    if (data) {
      setSlug(data.slug as string);
      setCountry((data.country as string) ?? '');
      setState((data.state as string) ?? '');
      setCity((data.city as string) ?? '');
      setAddress((data.public_address as string) ?? '');
      setMapsUrl((data.public_maps_url as string) ?? '');
      setPhone((data.public_phone as string) ?? '');
      setDescription((data.public_description as string) ?? '');
      setHours(((data.public_hours as Hour[]) ?? []));
      setTags(((data.public_tags as string[]) ?? []));
      setTemplate((data.public_template as string) ?? 'casual');
      setShowPrices((data.public_show_prices as boolean) ?? true);
      setPublished(Boolean(data.public_published));
    }
  }, [restaurant]);

  useEffect(() => { void load(); }, [load]);

  const complete = Boolean(country && state && city && address && description.trim().length >= DESC_MIN && hours.length > 0);

  async function save(nextPublished?: boolean) {
    if (!restaurant) return;
    const pub = nextPublished ?? published;
    const patch = {
      country, state, city,
      country_slug: slugify(country), state_slug: slugify(state), city_slug: slugify(city),
      public_address: address || null,
      public_maps_url: mapsUrl || null,
      public_phone: phone || null,
      public_description: description || null,
      public_hours: hours,
      public_tags: tags,
      public_template: template,
      public_show_prices: showPrices,
      public_published: complete ? pub : false
    };
    await supabase.from('restaurants').update(patch).eq('id', restaurant.id);
    setPublished(patch.public_published);
    setSavedMsg(true);
    window.setTimeout(() => setSavedMsg(false), 2000);
  }

  function toggleTag(tag: string) {
    setTags((p) => (p.includes(tag) ? p.filter((x) => x !== tag) : [...p, tag]));
  }
  function setHour(i: number, patch: Partial<Hour>) {
    setHours((p) => p.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  }
  function toggleHourDay(i: number, d: number) {
    setHours((p) => p.map((h, idx) => {
      if (idx !== i) return h;
      const days = h.days ?? [];
      return { ...h, days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort() };
    }));
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  const publicUrl = country && state && city
    ? `${window.location.origin}/${slugify(country)}/${slugify(state)}/${slugify(city)}/${slug}`
    : null;

  return (
    <div className="page">
      <h1 className="page__title">{t('publicsite.title')}</h1>
      {published && publicUrl
        ? <p className="page__subtitle">{t('publicsite.live_at')} <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a></p>
        : <p className="page__subtitle">{t('publicsite.intro')}</p>}

      <h2 className="section-title">{t('publicsite.geo')}</h2>
      <div className="form-row">
        <label className="field"><span>{t('publicsite.country')}</span>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">—</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="field"><span>{t('publicsite.state')}</span>
          <input value={state} maxLength={2} placeholder="SC" onChange={(e) => setState(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} />
        </label>
        <label className="field"><span>{t('publicsite.city')}</span><input value={city} onChange={(e) => setCity(e.target.value)} /></label>
      </div>
      <span className="muted-note">⚠ {t('publicsite.city_warn')}</span>
      <label className="field"><span>{t('publicsite.address')}</span><input value={address} onChange={(e) => setAddress(e.target.value)} /></label>
      <div className="form-row">
        <label className="field"><span>{t('publicsite.maps_url')}</span><input value={mapsUrl} onChange={(e) => setMapsUrl(e.target.value)} /></label>
        <label className="field"><span>{t('publicsite.phone')}</span><input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" /></label>
      </div>

      <h2 className="section-title">{t('publicsite.description')}</h2>
      <textarea className="field" rows={6} value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: '100%' }} />
      <span className="muted-note" style={{ color: description.trim().length >= DESC_MIN ? 'var(--color-verde)' : 'var(--color-muted)' }}>
        {description.trim().length}/{DESC_MIN} {t('publicsite.desc_min')}
      </span>
      <span className="muted-note">{t('publicsite.description_hint')}</span>

      <h2 className="section-title">{t('publicsite.hours')}</h2>
      {hours.map((h, i) => (
        <div key={i} className="schedule-box">
          <div className="day-toggles">
            {DOW.map((d) => (
              <button key={d} type="button" className={`day-chip${(h.days ?? []).includes(d) ? ' day-chip--on' : ''}`} onClick={() => toggleHourDay(i, d)}>{t(`menu.dow_${d}`)}</button>
            ))}
          </div>
          <div className="form-row" style={{ alignItems: 'flex-end' }}>
            <label className="field"><span>{t('menu.time_start')}</span><input type="time" value={h.start ?? ''} onChange={(e) => setHour(i, { start: e.target.value })} /></label>
            <label className="field"><span>{t('menu.time_end')}</span><input type="time" value={h.end ?? ''} onChange={(e) => setHour(i, { end: e.target.value })} /></label>
            <button type="button" className="link-button" onClick={() => setHours((p) => p.filter((_, idx) => idx !== i))}>×</button>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHours((p) => [...p, { days: [], start: '', end: '' }])} style={{ alignSelf: 'flex-start' }}>{t('publicsite.add_hour')}</button>

      <h2 className="section-title">{t('publicsite.tags')}</h2>
      <div className="day-toggles">
        {PUBLIC_TAGS.map((tag) => (
          <button key={tag} type="button" className={`day-chip${tags.includes(tag) ? ' day-chip--on' : ''}`} onClick={() => toggleTag(tag)}>
            {t(`pub.tag_${tag}`)}
          </button>
        ))}
      </div>

      <h2 className="section-title">{t('publicsite.template')}</h2>
      <div className="form-row">
        <select value={template} onChange={(e) => setTemplate(e.target.value)}>
          {TEMPLATES.map((tp) => (<option key={tp} value={tp}>{t(`publicsite.template_${tp}`)}</option>))}
        </select>
        <label className="adj">
          <input type="checkbox" checked={showPrices} onChange={(e) => setShowPrices(e.target.checked)} />
          {t('publicsite.show_prices')}
        </label>
      </div>

      <div className="form-row" style={{ marginTop: 16, alignItems: 'center' }}>
        <button type="button" className="btn btn--primary" onClick={() => void save()}>{savedMsg ? t('publicsite.saved') : t('publicsite.save')}</button>
        {complete ? (
          published
            ? <button type="button" className="btn btn--ghost" onClick={() => void save(false)}>{t('publicsite.unpublish')}</button>
            : <button type="button" className="btn btn--ghost" onClick={() => void save(true)}>{t('publicsite.publish')}</button>
        ) : (
          <span className="muted-note">{t('publicsite.incomplete')}</span>
        )}
        {published && publicUrl && <a className="link-button" href={publicUrl} target="_blank" rel="noreferrer">{t('publicsite.preview')}</a>}
      </div>
      {published && <p className="form-info" style={{ marginTop: 8 }}>{t('publicsite.published')}</p>}
    </div>
  );
}
